import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from 'react';
import styles from "../../../styles/components/Modal/Open-Deals/EditOrderModal.module.css";
import CloseIcon from "@mui/icons-material/Close";
import { fetchOrderById } from '../../../utils/api/Lead/OpenDeals/fetchOrderById';
import { updateOrder } from '../../../utils/api/Lead/OpenDeals/updateOrder';
import { closeOrder } from '../../../utils/api/Lead/OpenDeals/closeOrder';
import { deleteOrder } from '../../../utils/api/Lead/OpenDeals/deleteOrder';
import { getMyAccountApi } from '../../../utils/api/Account/getMyAccountApi';
import { LoadingCircle } from '../../UI/Custom/LoadingCircle';
import { InputSelector } from '../../UI/InputSelector';
import { DropdownSelector } from '../../UI/DropdownSelector';
import { Notification } from '../../UI/Notifications';
import { handleError } from '../../../utils/custom/errorHandler';
import { getOrderFormFields } from '../../../utils/fields/orderFormFields';
import { getAllAssetsApi } from '../../../utils/api/Lead/OpenDeals/getAllAssetsApi';
import { calculateOrderProfitApi } from '../../../utils/api/Lead/OpenDeals/calculateOrderProfitApi';
import { formatDateTime } from '../../../utils/custom/formatters';
import { LangContext } from '../../../context/LangContext';
import { ThemeContext } from '../../../context/ThemeContext';
import { ConfirmationModal } from '../../UI/Custom/ConfirmationModal';
import {
    calculateMarginFromVolume,
    calculateVolumeFromMargin,
    getAssetContractMultiplier,
    normalizeOrderVolume,
} from '../../../utils/orderFinancials';

const translations = {
    ru: {
        loadingAssetsError: "Не удалось загрузить список активов.",
        profit: "Прибыль",
        calculating: "Расчет...",
        loadingDataError: "Не удалось загрузить данные сделки.",
        close: "Закрыть",
        editOrder: "Редактировать сделку #",
        saving: "Сохранение...",
        saveChanges: "Сохранить изменения",
        closeDeal: "Закрыть сделку",
        deleteOrder: "Удалить ордер",
        deleting: "Удаление...",
        deleteTitle: "Удаление ордера",
        deleteMessage: (id) => `Вы уверены, что хотите безвозвратно удалить ордер #${id}?`,
        deleteConfirm: "Да, удалить",
        areYouSure: "Вы уверены?",
        yesClose: "Да, закрыть",
        noCancel: "Нет, отмена",
        cancel: "Отмена",
        hoverInfo: {
            admin: {
                id: "ID",
                firstName: "Имя",
                lastName: "Фамилия",
                email: "Email",
                role: "Роль",
                status: "Статус",
                created: "Создан"
            },
            date: {
                dateTime: "Дата и Время"
            },
            asset: {
                assetId: "ID Актива",
                platformId: "Platform ID",
                code: "Код",
                name: "Название",
                type: "Тип",
                price: "Цена"
            }
        }
    },
    en: {
        loadingAssetsError: "Failed to load asset list.",
        profit: "Profit",
        calculating: "Calculating...",
        loadingDataError: "Failed to load deal data.",
        close: "Close",
        editOrder: "Edit deal #",
        saving: "Saving...",
        saveChanges: "Save changes",
        closeDeal: "Close deal",
        deleteOrder: "Delete order",
        deleting: "Deleting...",
        deleteTitle: "Delete order",
        deleteMessage: (id) => `Are you sure you want to permanently delete order #${id}?`,
        deleteConfirm: "Yes, delete",
        areYouSure: "Are you sure?",
        yesClose: "Yes, close",
        noCancel: "No, cancel",
        cancel: "Cancel",
        hoverInfo: {
            admin: {
                id: "ID",
                firstName: "First Name",
                lastName: "Last Name",
                email: "Email",
                role: "Role",
                status: "Status",
                created: "Created"
            },
            date: {
                dateTime: "Date and Time"
            },
            asset: {
                assetId: "Asset ID",
                platformId: "Platform ID",
                code: "Code",
                name: "Name",
                type: "Type",
                price: "Price"
            }
        }
    },

};

const toDateTimeLocalValue = (value) => {
    if (!value) return '';

    const apiDate = /^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2})/.exec(value);
    if (apiDate) {
        return `${apiDate[3]}-${apiDate[2]}-${apiDate[1]}T${apiDate[4]}:${apiDate[5]}`;
    }

    const isoDate = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(value);
    return isoDate
        ? `${isoDate[1]}-${isoDate[2]}-${isoDate[3]}T${isoDate[4]}:${isoDate[5]}`
        : '';
};

export const EditOrderModal = ({ orderId, onClose, onOrderActionSuccess }) => {
    const modalRef = useRef(null);
    const financialSourceRef = useRef(null);
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const currentTranslation = translations[langSelected] || translations.ru;

    const [orderData, setOrderData] = useState(null);
    const [initialOrderData, setInitialOrderData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [hoveredInfo, setHoveredInfo] = useState(null);
    const [infoModalPosition, setInfoModalPosition] = useState({ x: 0, y: 0 });
    const [assets, setAssets] = useState([]);
    const [isConfirmingClose, setIsConfirmingClose] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [canDeleteOrder, setCanDeleteOrder] = useState(false);
    const [calculatedProfit, setCalculatedProfit] = useState(null);
    const [isCalculatingProfit, setIsCalculatingProfit] = useState(false);

    useEffect(() => {
        let isMounted = true;

        getMyAccountApi()
            .then((accountData) => {
                if (isMounted) {
                    setCanDeleteOrder(accountData?.permissions?.some(
                        (permission) => permission.name === 'lead.delete_orders'
                    ) ?? false);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setCanDeleteOrder(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        const loadAssets = async () => {
            try {
                const assetsData = await getAllAssetsApi();
                setAssets(assetsData);
            } catch (error) {
                handleError({ message: currentTranslation.loadingAssetsError }, setErrors);
            }
        };
        loadAssets();
    }, [currentTranslation]);

    const formFields = useMemo(() => getOrderFormFields(langSelected), [langSelected]);

    const hasChanges = useMemo(() => {
        if (!initialOrderData || !orderData) return false;
        const editableFields = formFields.filter(field => field.editable).map(field => field.key);

        for (const key of editableFields) {
            const currentVal = orderData[key] !== null ? String(orderData[key]) : null;
            const initialVal = initialOrderData[key] !== null ? String(initialOrderData[key]) : null;

            if (currentVal !== initialVal) {
                return true;
            }
        }
        return false;
    }, [orderData, initialOrderData, formFields]);

    const inputFieldsConfig = formFields.filter((field) => field.type === "input" && field.editable);
    const dateTimeFieldsConfig = formFields.filter((field) => field.type === "datetime-local" && field.editable);
    const dropdownFieldsConfig = formFields.filter((field) => field.type === "dropdown" && field.editable);

    const nonEditableFieldsConfig = useMemo(() => {
        const baseFields = formFields.filter((field) => !field.editable);
        if (!baseFields.some(field => field.key === 'profit')) {
            baseFields.push({ key: 'profit', label: currentTranslation.profit });
        }
        return baseFields;
    }, [formFields, currentTranslation]);


    const loadOrder = useCallback(async () => {
        setLoading(true);
        setErrors({});
        setTouched({});
        try {
            const data = await fetchOrderById(orderId);
            const normalizedData = {
                ...data,
                created_at: toDateTimeLocalValue(data.created_at),
                closed_at: toDateTimeLocalValue(data.closed_at),
            };
            setOrderData(normalizedData);
            setInitialOrderData(normalizedData);
            financialSourceRef.current = null;
            if (data.closed_at && data.profit !== undefined) {
                setCalculatedProfit(data.profit);
            } else {
                setCalculatedProfit(null);
            }
        } catch (err) {
            handleError(err.response?.data || err, setErrors);
        } finally {
            setLoading(false);
        }
    }, [orderId]);

    useEffect(() => {
        loadOrder();
    }, [loadOrder]);

    const calculateProfit = useCallback(async (currentOrderData) => {
        if (
            currentOrderData?.type &&
            currentOrderData?.assets_id &&
            currentOrderData?.open_price !== undefined &&
            currentOrderData?.open_price !== null &&
            currentOrderData?.leverage !== undefined &&
            currentOrderData?.leverage !== null &&
            currentOrderData?.volume !== undefined &&
            currentOrderData?.volume !== null
        ) {
            setIsCalculatingProfit(true);
            setErrors((prev) => {
                const newErrors = { ...prev };
                if (newErrors.profit) {
                    delete newErrors.profit;
                }
                return newErrors;
            });
            try {
                const orderProfitData = {
                    type: currentOrderData.type,
                    assets_id: parseInt(currentOrderData.assets_id),
                    leverage: parseInt(currentOrderData.leverage),
                    open_price: parseFloat(currentOrderData.open_price),
                    ...(currentOrderData.close_price !== undefined && currentOrderData.close_price !== null
                        ? { close_price: parseFloat(currentOrderData.close_price) }
                        : {}),
                    volume: parseFloat(currentOrderData.volume),
                    price_change_percent: ((v) => Number.isNaN(v) ? '' : v)(
                        parseFloat(currentOrderData.price_change_percent)
                    ),
                };
                const result = await calculateOrderProfitApi(orderProfitData);
                setCalculatedProfit(result.profit);
            } catch (err) {
                handleError(err.response?.data || err, setErrors, 'profit');
                setCalculatedProfit(null);
            } finally {
                setIsCalculatingProfit(false);
            }
        } else {
            setCalculatedProfit(null);
            setIsCalculatingProfit(false);
        }
    }, []);

    useEffect(() => {
        if (orderData) {
            const debounceTimer = setTimeout(() => {
                calculateProfit(orderData);
            }, 500);
            return () => clearTimeout(debounceTimer);
        } else if (orderData?.closed_at) {
            setCalculatedProfit(orderData.profit);
        }
    }, [
        orderData?.type,
        orderData?.assets_id,
        orderData?.open_price,
        orderData?.close_price,
        orderData?.leverage,
        orderData?.volume,
        orderData?.price_change_percent,
        calculateProfit
    ]);


    const handleChange = useCallback((key, value) => {
        if (['margin', 'volume'].includes(key)) {
            financialSourceRef.current = key;
        } else if (['open_price', 'leverage'].includes(key)) {
            financialSourceRef.current = 'volume';
        }

        setOrderData(prevData => {
            const fieldConfig = formFields.find(f => f.key === key);
            let parsedValue = value;

            if (value === '') {
                parsedValue = null;
            } else if (fieldConfig && fieldConfig.type === 'input') {
                const numValue = parseFloat(value);
                parsedValue = isNaN(numValue) ? value : numValue;
            }
            const nextData = { ...prevData, [key]: parsedValue };
            const selectedAsset = assets.find(
                asset => Number(asset.platform_id) === Number(nextData.assets_id)
            ) || nextData.asset;
            const multiplier = getAssetContractMultiplier(selectedAsset);

            if (key === 'margin' && parsedValue !== null) {
                const volume = calculateVolumeFromMargin(
                    parsedValue,
                    nextData.leverage,
                    nextData.open_price,
                    multiplier
                );

                if (volume !== null) {
                    nextData.volume = volume;
                }
            }

            if (key === 'volume' && parsedValue !== null) {
                const volume = normalizeOrderVolume(parsedValue);
                const margin = calculateMarginFromVolume(
                    volume,
                    nextData.leverage,
                    nextData.open_price,
                    multiplier
                );

                if (volume !== null) {
                    nextData.volume = volume;
                }

                if (margin !== null) {
                    nextData.margin = margin;
                }
            }

            if (['open_price', 'leverage'].includes(key) && parsedValue !== null) {
                const margin = calculateMarginFromVolume(
                    nextData.volume,
                    nextData.leverage,
                    nextData.open_price,
                    multiplier
                );

                if (margin !== null) {
                    nextData.margin = margin;
                }
            }

            return nextData;
        });

        setTouched((prev) => ({ ...prev, [key]: true }));

        setErrors((prev) => {
            const newErrors = { ...prev };
            if (newErrors[key]) {
                delete newErrors[key];
            }
            if (['margin', 'volume'].includes(key)) {
                delete newErrors.margin;
                delete newErrors.volume;
            }
            if (newErrors.general) {
                delete newErrors.general;
            }
            if (newErrors.profit) {
                delete newErrors.profit;
            }
            return newErrors;
        });
    }, [assets, formFields]);

    const handleFocus = useCallback((key) => {
        setTouched((prev) => ({ ...prev, [key]: true }));

        setErrors((prev) => {
            const newErrors = { ...prev };
            if (newErrors[key]) {
                delete newErrors[key];
            }
            if (newErrors.general) {
                delete newErrors.general;
            }
            return newErrors;
        });
    }, []);

    const handleBlur = useCallback((key) => {
        setTouched((prev) => ({ ...prev, [key]: true }));
    }, []);

    const handleSave = async () => {
        setErrors({});
        setIsSaving(true);
        try {
            const changes = {};
            const editableFields = formFields.filter(field => field.editable);

            for (const field of editableFields) {
                const key = field.key;
                const currentVal = orderData[key] !== null ? String(orderData[key]) : null;
                const initialVal = initialOrderData[key] !== null ? String(initialOrderData[key]) : null;

                if (currentVal !== initialVal) {
                    changes[key] = orderData[key];
                }
            }

            if (financialSourceRef.current === 'margin' && changes.margin !== undefined) {
                delete changes.volume;
            }

            if (financialSourceRef.current === 'volume') {
                delete changes.margin;
            }

            if (Object.keys(changes).length > 0) {
                await updateOrder(orderId, changes);
                onOrderActionSuccess();
                onClose();
            } else {
                onClose();
            }
        } catch (err) {
            handleError(err.response?.data || err, setErrors);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteClick = () => {
        setIsConfirmingClose(true);
    };

    const handleConfirmClose = async () => {
        setIsClosing(true);
        setErrors({});
        try {
            await closeOrder(orderId);
            onOrderActionSuccess();
            onClose();
        } catch (err) {
            handleError(err.response?.data || err, setErrors);
        } finally {
            setIsClosing(false);
            setIsConfirmingClose(false);
        }
    };

    const handleCancelClose = () => {
        setIsConfirmingClose(false);
    };

    const handleCloseDeleteConfirm = () => {
        if (!isDeleting) {
            setIsDeleteConfirmOpen(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (isDeleting) return;

        setIsDeleting(true);
        setErrors({});

        try {
            await deleteOrder(orderId);
            onOrderActionSuccess();
            onClose();
        } catch (err) {
            handleError(err.response?.data || err, setErrors);
        } finally {
            setIsDeleting(false);
            setIsDeleteConfirmOpen(false);
        }
    };

    const handleMouseEnterInfo = useCallback((event, data, type) => {
        const t = currentTranslation.hoverInfo;
        let displayData = {};
        if (type === 'admin') {
            displayData = {
                [t.admin.id]: data.id,
                [t.admin.firstName]: data.first_name || '-',
                [t.admin.lastName]: data.last_name || '-',
                [t.admin.email]: data.email,
                [t.admin.role]: data.role?.name || '-',
                [t.admin.status]: data.status,
                [t.admin.created]: formatDateTime(data.created_at),
            };
        } else if (type === 'date') {
            displayData = { [t.date.dateTime]: formatDateTime(data) };
        } else if (type === 'asset') {
            displayData = {
                [t.asset.assetId]: data.id,
                [t.asset.platformId]: data.platform_id,
                [t.asset.code]: data.code,
                [t.asset.name]: data.name,
                [t.asset.type]: data.type,
                [t.asset.price]: data.price,
            };
        }
        setHoveredInfo({ data: displayData, type: type });
        setInfoModalPosition({ x: event.clientX - 100, y: event.clientY + 15 });
    }, [currentTranslation]);

    const handleMouseLeaveInfo = useCallback(() => {
        setHoveredInfo(null);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isDeleteConfirmOpen || isDeleting) {
                return;
            }

            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose, isDeleteConfirmOpen, isDeleting]);

    const modalClass = theme === 'dark' ? `${styles.modal} ${styles.darkModal}` : styles.modal;
    const filtersContainerClass = theme === 'dark' ? `${styles.filtersContainer} ${styles.darkFiltersContainer}` : styles.filtersContainer;
    const modalLabelClass = theme === 'dark' ? `${styles.modalLabel} ${styles.darkModalLabel}` : styles.modalLabel;
    const modalContentClass = theme === 'dark' ? `${styles.modalContent} ${styles.darkModalContent}` : styles.modalContent;
    const inputRowLabelClass = theme === 'dark' ? styles.darkLabel : '';
    const formInputClass = theme === 'dark' ? styles.darkFormInput : '';
    const hoverInfoModalClass = theme === 'dark' ? styles.darkHoverInfoModal : '';
    const loadingContainerClass = theme === 'dark' ? styles.darkLoadingContainer : '';
    const modalFooterClass = theme === 'dark' ? `${styles.modalFooter} ${styles.darkModalFooter}` : styles.modalFooter;
    const confirmTextColor = theme === 'dark' ? styles.darkConfirmText : '';

    if (!orderData && !loading && Object.keys(errors).length > 0) {
        return (
            <div className={modalClass}>
                <div className={filtersContainerClass}>
                    <p className={`${styles.error} ${theme === 'dark' ? styles.darkError : ''}`}>{errors.general || currentTranslation.loadingDataError}</p>
                    <button className={`${styles.cancelButton} ${theme === 'dark' ? styles.darkCancelButton : ''}`} onClick={onClose}>{currentTranslation.close}</button>
                </div>
            </div>
        );
    }

    const isSaveButtonDisabled = !hasChanges || isSaving || isClosing || loading || isConfirmingClose || isCalculatingProfit || !!errors.profit;
    const isOrderClosed = initialOrderData?.close_price != null;

    return (
        <div className={modalClass}>
            {(errors.general || errors.profit) && <Notification message={errors.general || errors.profit} type={"error"} />}
            <div className={filtersContainerClass} ref={modalRef}>
                <div className={modalLabelClass}>
                    <p>{currentTranslation.editOrder}{orderId}</p>
                    <div onClick={onClose}>
                        <CloseIcon className={theme === 'dark' ? styles.darkCloseIcon : ''} />
                    </div>
                </div>

                {loading ? (
                    <div className={`${styles.loadingContainer} ${loadingContainerClass}`}>
                        <LoadingCircle size={30} thickness={1} />
                    </div>
                ) : (
                    <div className={modalContentClass}>
                        {dropdownFieldsConfig.map((field) => (
                            <div
                                key={field.key}
                                className={styles.inputRow}
                                style={{ gridColumn: 'span 3' }}
                            >
                                <label className={inputRowLabelClass}>{field.label}:</label>
                                <DropdownSelector
                                    filterName={field.key}
                                    selectedValue={orderData?.[field.key] || ''}
                                    handleFilterChange={handleChange}
                                    options={field.options || []}
                                    onFocus={() => handleFocus(field.key)}
                                    onBlur={() => handleBlur(field.key)}
                                    hasError={!!errors[field.key] && !!touched[field.key]}
                                    errorMessage={errors[field.key]}
                                    isTouched={!!touched[field.key]}
                                    disabled={isOrderClosed}
                                    theme={theme}
                                />
                            </div>
                        ))}

                        {inputFieldsConfig
                            .filter((field) => field.key !== 'close_price' || isOrderClosed)
                            .map((field) => (
                            <div
                                key={field.key}
                                className={styles.inputRow}
                                style={{ gridColumn: 'span 3' }}
                            >
                                <label className={inputRowLabelClass}>{field.label}:</label>
                                <InputSelector
                                    filterName={field.key}
                                    selectedValue={orderData?.[field.key] === null ? '' : orderData?.[field.key]}
                                    handleFilterChange={handleChange}
                                    onFocus={() => handleFocus(field.key)}
                                    onBlur={() => handleBlur(field.key)}
                                    hasError={!!errors[field.key] && !!touched[field.key]}
                                    errorMessage={errors[field.key]}
                                    type={'text'}
                                    isTouched={!!touched[field.key]}
                                    placeholder={field.placeholder}
                                    disabled={
                                        isOrderClosed &&
                                        !['open_price', 'close_price', 'leverage', 'volume', 'margin'].includes(field.key)
                                    }
                                    theme={theme}
                                />
                            </div>
                        ))}

                        {dateTimeFieldsConfig.map((field) => (
                            <div
                                key={field.key}
                                className={styles.inputRow}
                                style={{ gridColumn: 'span 3' }}
                            >
                                <label className={inputRowLabelClass}>{field.label}:</label>
                                <InputSelector
                                    filterName={field.key}
                                    selectedValue={orderData?.[field.key] || ''}
                                    handleFilterChange={handleChange}
                                    onFocus={() => handleFocus(field.key)}
                                    onBlur={() => handleBlur(field.key)}
                                    hasError={!!errors[field.key] && !!touched[field.key]}
                                    errorMessage={errors[field.key]}
                                    type={field.type}
                                    isTouched={!!touched[field.key]}
                                    disabled={field.key === 'closed_at' && !isOrderClosed}
                                    theme={theme}
                                />
                            </div>
                        ))}

                        {nonEditableFieldsConfig.map((field) => (
                            <div key={field.key} className={styles.inputRow} style={{ gridColumn: 'span 3' }}>
                                <label className={inputRowLabelClass}>{field.label}:</label>
                                <input
                                    type="text"
                                    value={
                                        field.key === 'assets_id'
                                            ? assets.find(asset => asset.platform_id === orderData?.assets_id)?.name || '-'
                                            : (['closed_at', 'created_at'].includes(field.key)
                                                ? formatDateTime(orderData?.[field.key])
                                                : (field.key === 'created_by_admin'
                                                    ? (orderData?.created_by_admin ? `${orderData.created_by_admin.email}` : '-')
                                                    : (
                                                        field.key === 'profit'
                                                            ? (isCalculatingProfit ? currentTranslation.calculating : (calculatedProfit !== null ? calculatedProfit : '-'))
                                                            : (orderData?.[field.key] !== null ? orderData?.[field.key] : '-')
                                                    )
                                                )
                                            )
                                    }
                                    className={`${styles.formInput} ${formInputClass} ${field.key === 'profit' && isCalculatingProfit ? styles.calculatingProfit : ''}`}
                                    readOnly
                                    onMouseEnter={(e) => {
                                        if (field.key === 'created_by_admin' && orderData?.created_by_admin) {
                                            handleMouseEnterInfo(e, orderData.created_by_admin, 'admin');
                                        } else if (['closed_at', 'created_at'].includes(field.key) && orderData?.[field.key]) {
                                            handleMouseEnterInfo(e, orderData[field.key], 'date');
                                        }
                                        else if (field.key === 'assets_id' && orderData?.assets_id) {
                                            const selectedAsset = assets.find(asset => asset.platform_id === orderData.assets_id);
                                            if (selectedAsset) {
                                                handleMouseEnterInfo(e, {
                                                    id: selectedAsset.id,
                                                    platform_id: selectedAsset.platform_id,
                                                    code: selectedAsset.code,
                                                    name: selectedAsset.name,
                                                    type: selectedAsset.type,
                                                    price: selectedAsset.price,
                                                }, 'asset');
                                            }
                                        }
                                    }}
                                    onMouseLeave={handleMouseLeaveInfo}
                                />
                            </div>
                        ))}
                    </div>
                )}

                <div className={modalFooterClass}>
                    <div className={styles.confirmationWrapper}>
                        {!isConfirmingClose && canDeleteOrder && (
                            <button
                                type="button"
                                className={`${styles.deleteButton} ${theme === 'dark' ? styles.darkDeleteButton : ''}`}
                                onClick={() => setIsDeleteConfirmOpen(true)}
                                disabled={isSaving || isClosing || isDeleting || loading}
                            >
                                {isDeleting ? currentTranslation.deleting : currentTranslation.deleteOrder}
                            </button>
                        )}
                        {!isConfirmingClose && (
                            <button
                                type="button"
                                className={`${styles.submitButton} ${theme === 'dark' ? styles.darkSubmitButton : ''}`}
                                onClick={handleSave}
                                disabled={isSaveButtonDisabled}
                            >
                                {isSaving ? currentTranslation.saving : currentTranslation.saveChanges}
                            </button>
                        )}
                        {!isOrderClosed && (
                            <button
                                type="button"
                                className={`${styles.deleteButton} ${theme === 'dark' ? styles.darkDeleteButton : ''}`}
                                onClick={handleDeleteClick}
                                disabled={isSaving || isClosing || loading || isConfirmingClose}
                            >
                                {currentTranslation.closeDeal}
                            </button>
                        )}
                        {isConfirmingClose && (
                            <>
                                <p className={`${styles.confirmText} ${confirmTextColor}`}>{currentTranslation.areYouSure}</p>
                                <button
                                    type="button"
                                    className={`${styles.confirmButton} ${theme === 'dark' ? styles.darkConfirmButton : ''}`}
                                    onClick={handleConfirmClose}
                                    disabled={isClosing}
                                >
                                    {isClosing ? currentTranslation.saving : currentTranslation.yesClose}
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.cancelButton} ${theme === 'dark' ? styles.darkCancelButton : ''}`}
                                    onClick={handleCancelClose}
                                    disabled={isClosing}
                                >
                                    {currentTranslation.noCancel}
                                </button>
                            </>
                        )}
                        {!isConfirmingClose && (
                            <button
                                type="button"
                                className={`${styles.cancelButton} ${theme === 'dark' ? styles.darkCancelButton : ''}`}
                                onClick={onClose}
                                disabled={isSaving || isClosing || loading}
                            >
                                {currentTranslation.cancel}
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {hoveredInfo && (
                <div
                    className={`${styles.hoverInfoModal} ${hoverInfoModalClass}`}
                    style={{ left: infoModalPosition.x, top: infoModalPosition.y }}
                >
                    {Object.entries(hoveredInfo.data).map(([key, value]) => (
                        <p key={key}><strong>{key}:</strong> {value}</p>
                    ))}
                </div>
            )}
            {isDeleteConfirmOpen && (
                <ConfirmationModal
                    isOpen={isDeleteConfirmOpen}
                    onClose={handleCloseDeleteConfirm}
                    onConfirm={handleConfirmDelete}
                    title={currentTranslation.deleteTitle}
                    message={currentTranslation.deleteMessage(orderId)}
                    confirmText={isDeleting ? currentTranslation.deleting : currentTranslation.deleteConfirm}
                    cancelText={currentTranslation.cancel}
                />
            )}
        </div>
    );
};
